/**
 * @file ShopCardLayout.tsx
 * @description Карточка магазина платформы: шапка и вкладки «Обзор /
 * Домены / Интеграции / Доступ» (phase19.28 этап А, v0.19.63;
 * uiux-platform-shops §4–§6; вложенные маршруты `/platform/shops/:shopId/<tab>`).
 *
 * Каркас грузит карточку и отдаёт её вкладкам (`Outlet context`), вкладка
 * — отдельный маршрут, поэтому адрес вкладки можно открыть и переслать.
 * Шапка: название, статус сайта, «Витрина ↗», «Кабинет ↗» (вход под
 * владельцем с причиной, PL17), в «⋯» — «Изменить сведения» и
 * «Отключить магазин» (В8). Права вкладок — реестр `SHOP_TABS`.
 */

import { Store } from "lucide-react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router";

import { useCurrentContext } from "../../../auth/hooks";
import { useShopCardActions } from "../../../hooks/platform/useShopCardActions";
import { useShopCardResource, type ShopCardContext } from "../../../hooks/platform/useShopCard";
import { usePlatformResource } from "../../../hooks/platform/usePlatformResource";
import { SITE_STATE_META, siteState } from "../../../lib/platform/shop-list-filter";
import { loadTimeZoneOptions } from "../../../lib/platform/time-zone-options";
import { ShopCabinetEntry } from "../../../components/platform/shops/ShopCabinetEntry";
import { ShopEditDialog } from "../../../components/platform/shops/ShopEditDialog";
import { tabFromPath, visibleTabs, type ShopTab } from "../../../components/platform/shops/shop-card-tabs";
import { CabinetPageHeader } from "../../../components/ui/CabinetPageHeader";
import { ConfirmDialog } from "../../../components/ui/ConfirmDialog";
import { EmptyState } from "../../../components/ui/EmptyState";
import { ListError } from "../../../components/ui/ListStates";
import { Skeleton } from "../../../components/ui/skeleton";
import { StatusPill } from "../../../components/ui/StatusPill";
import { Tabs } from "../../../components/ui/Tabs";

const BACK = { to: "/platform/shops", label: "К магазинам" };

export function ShopCardLayout() {
  const { shopId = "" } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const permissions = useCurrentContext()?.role.permissions ?? [];
  const card = useShopCardResource(shopId);
  const shop = card.data && card.data.id === shopId ? card.data : null;
  const actions = useShopCardActions(shop, card.reload);
  const zones = usePlatformResource(actions.editing ? "time-zones" : null, loadTimeZoneOptions, "Не удалось загрузить пояса");
  const base = `/platform/shops/${shopId}`;
  const tabs = visibleTabs(permissions);
  const tab = tabFromPath(location.pathname);

  if (!shop) {
    if (card.errorStatus === 404) {
      return (
        <div className="flex flex-col gap-4">
          <CabinetPageHeader title="Магазин не найден" back={BACK} />
          <EmptyState icon={Store} title="Такого магазина нет" description="Возможно, адрес набран с ошибкой." />
        </div>
      );
    }
    return (
      <div className="flex flex-col gap-4">
        <CabinetPageHeader title="Магазин" back={BACK} />
        {card.error ? (
          <ListError message={card.error} onRetry={card.reload} />
        ) : (
          <div aria-busy="true" aria-label="Загрузка магазина" className="flex flex-col gap-3">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        )}
      </div>
    );
  }

  const context: ShopCardContext = { shop, base, reload: card.reload, enterCabinet: actions.enterCabinet };

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <CabinetPageHeader
        title={shop.displayName}
        subtitle={shop.slug}
        status={<StatusPill meta={SITE_STATE_META[siteState(shop)]} />}
        back={BACK}
        secondary={actions.secondary}
        menu={actions.menu}
        menuLabel="Действия с магазином"
      />
      {card.error ? <ListError message={card.error} onRetry={card.reload} /> : null}
      <Tabs<ShopTab>
        aria-label="Разделы магазина"
        className="shrink-0"
        items={tabs.map((t) => ({ value: t.value, label: t.label, count: t.value === "domains" ? shop.domainsCount : undefined }))}
        value={tab}
        onChange={(next) => navigate(`${base}/${next}`)}
      />
      <div role="tabpanel" aria-label={tabs.find((t) => t.value === tab)?.label} className="min-w-0">
        <Outlet context={context} />
      </div>

      {actions.editing ? (
        <ShopEditDialog
          shop={shop}
          timeZones={zones.data ?? []}
          onClose={() => actions.setEditing(false)}
          onSaved={() => {
            actions.setEditing(false);
            card.reload();
          }}
        />
      ) : null}
      <ShopCabinetEntry ownerId={actions.cabinetOwnerId} shopId={shop.id} onClose={actions.closeCabinet} />
      <ConfirmDialog
        request={actions.confirmApi.request}
        onSettle={actions.confirmApi.settle}
        onDismiss={actions.confirmApi.dismiss}
      />
    </div>
  );
}
